import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
    Box,
    Divider,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { Query } from '@nozbe/watermelondb';
import { useFormik } from 'formik';
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { InvoicesAnalytics } from 'shared/analytics/invoices';
import { CountryCode } from 'shared/constants/countries';
import { getInvoiceTaxesList } from 'shared/constants/invoices/INVOICE_TAXES';
import { INVOICE_STATUS } from 'shared/constants/invoices/statuses';
import Entry from 'shared/db/services/Entry';
import Invoice from 'shared/db/services/Invoice';
import { ContactModel } from 'shared/types/Contacts';
import { EntryModel } from 'shared/types/Entries';
import { HorseModel } from 'shared/types/Horses';
import {
    InvoiceFinancialDetails,
    InvoicePayload,
    ParsedInvoiceProduct,
} from 'shared/types/Invoice';
import { addAlphaToColor } from 'shared/utils/colors';
import { checkIfObjectHasEmptyField } from 'shared/utils/data';
import {
    calculateInvoiceDetailsFromItems,
    canEditInvoiceEntries,
    getDefaultInvoiceDueDays,
} from 'shared/utils/invoicing';
import Moment from 'shared/utils/moment';

import { InvoiceIcon } from '@/assets/svg';
import { SelectContactsModal } from '@/components/SelectContactsSection/components';
import { WEB_STORAGE_KEYS } from '@/constants/webStorageKeys';
import { useDBSyncContext } from '@/context/DBSyncContext';
import { useImagesContext } from '@/context/ImagesContext';
import { useInvoicingSystemContext } from '@/context/InvoicingSystemContext';
import { useOrganisationsContext } from '@/context/OrganisationsContext';
import { useUserContext } from '@/context/UserContext';
import {
    useBrowserExitPrompt,
    useDatabase,
    useCancelEntityFormAlert,
    useEscapeButton,
    useAccountingProvider,
} from '@/hooks';
import { useEntityImage } from '@/hooks/useEntityImage';
import { useOrganisationData } from '@/hooks/useOrganisationData';
import { useRegion } from '@/hooks/useRegion';
import { databaseValidation } from '@/services/databaseValidation';
import { FirebaseAnalytics } from '@/services/firebase/analytics';
import Logger from '@/services/logger';
import { dbServiceLogger } from '@/services/logger/logger';
import { Snackbar } from '@/services/toastNotifications';
import {
    getItemFromLocalStorage,
    storeItemInLocalStorage,
} from '@/services/webStorage/localStorage';
import regionStore from '@/stores/userRegion';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';
import { getImageDisplayUrl } from '@/utils/images';

import AddEditContactModal from '../AddEditContactModal';
import AddEditEntryModal from '../AddEditEntryModal';
import AddEditHorseModal from '../AddEditHorseModal';
import AddEditModalHeader from '../AddEditModalHeader';
import { BackdropLoader } from '../Loaders';
import Modal from '../Modal';
import RemoveEntityAlert from '../RemoveEntityAlert';
import SelectContactsSection from '../SelectContactsSection';
import SelectEntriesSection from '../SelectEntriesSection';
import SelectInvoicingProductsSection from '../SelectInvoicingProductsSection';
import TooltipSyncButton from '../TooltipButton';

import RemoveContactAlert from './components';
import { AddEditInvoiceFormValues, DueDateUnit, Props } from './types';

const MAX_DUE_DAYS = 365;
const MAX_DUE_WEEKS = 52;
const DEFAULT_DUE_VALUE = 30;
const DEFAULT_DUE_UNIT: DueDateUnit = 'days';

function AddEditInvoiceModal({
    isEditMode,
    close,
    onSave,
    closeOnSubmit = false,
    invoice,
    onRemove,
    entry,
    contact,
}: Props) {
    const { t } = useTranslation();
    const { isSyncInProgress } = useDBSyncContext();
    const { ImagesService } = useImagesContext();
    const { userProfileData } = useUserContext();
    const { getDatabase } = useDatabase();
    const { userOrganisation, userOrganisations } = useOrganisationsContext();
    const accountingProvider = useAccountingProvider();
    const { isInvoiceSystemEnabled } = useInvoicingSystemContext();
    const userRegion = useRegion();
    const { organisation: organisationData } = useOrganisationData();

    const teamAvatar = useEntityImage(userOrganisation?.id || '');

    // INFO: this hook open browser prompt when we refresh or leave the browser
    // so the user dont lose form information without confirmation
    useBrowserExitPrompt();

    const database = useMemo(() => getDatabase(), [getDatabase]);

    const invoiceService = useMemo(
        () =>
            new Invoice({
                database,
                imageService: ImagesService,
                logger: dbServiceLogger,
                validation: databaseValidation,
            }),
        [ImagesService, database],
    );

    const entriesService = useMemo(
        () =>
            new Entry({
                database,
                imageService: ImagesService,
                logger: dbServiceLogger,
                validation: databaseValidation,
            }),
        [database, ImagesService],
    );

    const invoiceTaxesList = useMemo(() => getInvoiceTaxesList(t), [t]);

    const [isCreating, setIsCreating] = useState(false);
    const [isRemoveContactAlertOpen, setIsRemoveContactAlertOpen] =
        useState(false);
    const [isRemoveInvoiceAlertOpen, setIsRemoveInvoiceAlertOpen] =
        useState(false);
    const [isSelectEntryContactModalOpen, setIsSelectEntryContactModalOpen] =
        useState(false);
    const [preSelectedContact, setPreSelectedContact] =
        useState<ContactModel | null>(null);
    const [preSelectedEntryHorse, setPreSelectedEntryHorse] =
        useState<HorseModel | null>(null);

    const openRemoveContactAlert = useCallback(
        () => setIsRemoveContactAlertOpen(true),
        [],
    );

    const closeRemoveContactAlert = useCallback(
        () => setIsRemoveContactAlertOpen(false),
        [],
    );

    const openRemoveInvoiceAlert = useCallback(
        () => setIsRemoveInvoiceAlertOpen(true),
        [],
    );

    const closeRemoveInvoiceAlert = useCallback(
        () => setIsRemoveInvoiceAlertOpen(false),
        [],
    );

    const openSelectEntryContactModal = useCallback(
        () => setIsSelectEntryContactModalOpen(true),
        [],
    );

    const selectEntryContact = useCallback(async () => {
        const contactsCount =
            await preSelectedEntryHorse?.contacts.fetchCount();
        if (contactsCount && contactsCount > 1) {
            openSelectEntryContactModal();
        } else if (preSelectedEntryHorse) {
            const [contactToAssign] =
                await preSelectedEntryHorse.contacts.fetch();
            setPreSelectedContact(contactToAssign);
        }
    }, [openSelectEntryContactModal, preSelectedEntryHorse]);

    const closeSelectEntryContactModal = useCallback(
        () => setIsSelectEntryContactModalOpen(false),
        [],
    );

    const applyTaxByDefault = useMemo(() => {
        const applyTax = getItemFromLocalStorage<boolean>(
            WEB_STORAGE_KEYS.apply_tax_to_invoice,
        );

        return applyTax;
    }, []);

    const [initialValues, setInitialValues] =
        useState<AddEditInvoiceFormValues>(() => ({
            applyTax: applyTaxByDefault ? 'included' : 'excluded',
            contact: null,
            dueDate: isInvoiceSystemEnabled
                ? Moment().add(
                      getDefaultInvoiceDueDays(
                          userOrganisation?.default_invoice_due_days,
                      ),
                      'days',
                  )
                : Moment().add(1, 'M'),
            attachReport: false,
            entries: [],
            invoiceDate: Moment(),
            dueDateValue: DEFAULT_DUE_VALUE,
            dueDateUnit: DEFAULT_DUE_UNIT,
            productsTaxRate:
                organisationData?.productsTaxRate ||
                organisationData?.taxRate ||
                undefined,
            taxRate: organisationData?.taxRate || undefined,
            footerText: organisationData?.footerMessage || '',
            useCustomFooter: false,
            selectedProducts: [],
        }));

    const hasUserSetDueDateRef = useRef(false);

    // Recomputes dueDate once userOrganisation resolves, since it could be null on the first render.
    useEffect(() => {
        if (isEditMode || hasUserSetDueDateRef.current) {
            return;
        }

        setInitialValues((previous) => ({
            ...previous,
            dueDate: Moment().add(
                getDefaultInvoiceDueDays(
                    userOrganisation?.default_invoice_due_days,
                ),
                'days',
            ),
        }));
    }, [isEditMode, userOrganisation?.default_invoice_due_days]);

    const [entriesToSelectQuery, setEntriesToSelectQuery] =
        useState<Query<EntryModel> | null>(null);

    const prepareInitialValues = useCallback(async () => {
        if (isEditMode && invoice) {
            try {
                const entries = await invoice.entries.fetch();
                const invoiceContact = (await invoice.contacts.fetch())[0];
                const invoiceProducts = await invoice.products.fetch();

                const invoiceDate = invoice.invoiceDate
                    ? Moment(invoice.invoiceDate)
                    : Moment(invoice.createdAt);
                const dueDate = Moment(invoice.dueDateTime);

                const diffDays = dueDate.diff(invoiceDate, 'days');
                const diffWeeks = dueDate.diff(invoiceDate, 'weeks');
                const dueDateUnit: DueDateUnit =
                    diffDays % 7 === 0 && diffWeeks > 0 ? 'weeks' : 'days';
                const dueDateValue =
                    dueDateUnit === 'weeks' ? diffWeeks : diffDays;

                const parsedProducts: ParsedInvoiceProduct[] =
                    invoiceProducts.map((p) => ({
                        id: p.id,
                        invoicingProductId: p.invoicingProductId,
                        name: p.name,
                        price: p.price,
                        userId: p.userId,
                        organisationId: p.organisationId,
                        quantity: p.quantity,
                    }));

                const organisationFooter =
                    organisationData?.footerMessage || '';
                const hasCustomFooter =
                    !!invoice.footerText &&
                    invoice.footerText !== organisationFooter;

                setInitialValues({
                    applyTax: invoice.applyTaxes ? 'included' : 'excluded',
                    contact: invoiceContact,
                    entries,
                    attachReport: invoice.attachReports,
                    dueDate,
                    invoiceDate,
                    dueDateValue: dueDateValue || DEFAULT_DUE_VALUE,
                    dueDateUnit,
                    taxRate: invoice.taxRate || undefined,
                    productsTaxRate: invoice.productsTaxRate || undefined,
                    footerText: invoice.footerText || organisationFooter,
                    useCustomFooter: hasCustomFooter,
                    selectedProducts: parsedProducts,
                });
            } catch {
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: {
                        variant: 'error',
                    },
                });
            }
        } else if (entry && preSelectedContact) {
            setInitialValues((values) => ({
                ...values,
                entries: [entry],
                contact: preSelectedContact,
            }));
        } else if (contact) {
            setInitialValues((values) => ({
                ...values,
                contact,
            }));
        }
    }, [
        contact,
        entry,
        invoice,
        isEditMode,
        preSelectedContact,
        t,
        organisationData?.footerMessage,
    ]);

    const getPreSelectedEntryHorse = useCallback(async () => {
        if (!isEditMode && entry) {
            const horse = await entry.horse.fetch();

            if (horse) {
                setPreSelectedEntryHorse(horse);
            } else {
                openSelectEntryContactModal();
            }
        }
    }, [entry, isEditMode, openSelectEntryContactModal]);

    useEffect(() => {
        getPreSelectedEntryHorse();
    }, [getPreSelectedEntryHorse]);

    useEffect(() => {
        selectEntryContact();
    }, [selectEntryContact]);

    useEffect(() => {
        prepareInitialValues();
    }, [isEditMode, prepareInitialValues]);

    // Update tax rates and footer from local DB when organisation data loads
    useEffect(() => {
        if (!isEditMode && organisationData) {
            setInitialValues((prev) => ({
                ...prev,
                taxRate: organisationData.taxRate || prev.taxRate,
                productsTaxRate:
                    organisationData.productsTaxRate ||
                    organisationData.taxRate ||
                    prev.productsTaxRate,
                footerText:
                    prev.footerText || organisationData.footerMessage || '',
            }));
        }
    }, [isEditMode, organisationData]);

    const addUpdateInvoice = useCallback(
        async (values: AddEditInvoiceFormValues) => {
            try {
                setIsCreating(true);

                if (!userProfileData) {
                    throw new Error();
                }

                const userId = userProfileData.id || '';

                const {
                    applyTax,
                    dueDate,
                    contact,
                    entries,
                    attachReport,
                    selectedProducts,
                    taxRate,
                    invoiceDate,
                    productsTaxRate,
                    footerText,
                } = values;

                const selectedEntriesIds = entries.map((entry) => ({
                    id: entry.id,
                }));

                const payload: InvoicePayload = {
                    applyTaxes: applyTax === 'included',
                    dueDateTime: dueDate.toISOString(),
                    contactId: contact?.id ?? '',
                    provider: accountingProvider || 'internal',
                    sentToContact: false,
                    status: INVOICE_STATUS.draft,
                    selectedEntriesIds,
                    selectedProducts: selectedProducts || [],
                    attachReports: attachReport,
                    taxRate,
                    ...(isInvoiceSystemEnabled && {
                        invoiceDate: invoiceDate.toISOString(),
                        productsTaxRate,
                        footerText: footerText || undefined,
                        invoicingSystem: true,
                    }),
                };

                let message;

                if (!isEditMode) {
                    const createdInvoice = await invoiceService.add({
                        payload,
                        userId,
                    });

                    InvoicesAnalytics.logUserCreatedInvoice(
                        FirebaseAnalytics.logEvent,
                        {
                            invoice: createdInvoice,
                            isOwner: userOrganisation?.owner ?? true,
                        },
                    );

                    if (onSave) {
                        onSave(createdInvoice);
                    }
                    message = 'Invoice:created';
                } else {
                    if (!invoice) {
                        throw new Error();
                    }

                    const previousAttachReports = invoice.attachReports;

                    const updatedInvoice = await invoiceService.update(
                        invoice.id,
                        payload,
                    );

                    InvoicesAnalytics.logUserUpdatedInvoice(
                        FirebaseAnalytics.logEvent,
                        {
                            invoice: updatedInvoice,
                            previousAttachReports,
                        },
                    );

                    message = 'Invoice:updated';
                }

                Snackbar.showToastNotification({
                    message: t(message),
                });

                if (isEditMode || closeOnSubmit) {
                    close();
                }
            } catch (error) {
                Logger.logError(`(addUpdateInvoice) ${error}`);
                setIsCreating(false);
                Snackbar.showToastNotification({
                    message: t('App:Messages:something_went_wrong'),
                    options: {
                        variant: 'error',
                    },
                });
            }
        },
        [
            userProfileData,
            accountingProvider,
            isInvoiceSystemEnabled,
            isEditMode,
            t,
            closeOnSubmit,
            invoiceService,
            userOrganisation?.owner,
            onSave,
            invoice,
            close,
        ],
    );

    const handleSubmit = useCallback(
        async (values: AddEditInvoiceFormValues) => {
            const entries = values.entries;
            let hasProceduresAssigned = true;
            let hasHorseAssigned = true;

            const hasProducts = (values.selectedProducts?.length || 0) > 0;

            const hasEmptyFields = checkIfObjectHasEmptyField(values, [
                'attachReport',
                'selectedProducts',
                'taxRate',
                'productsTaxRate',
                'footerText',
                'useCustomFooter',
                'dueDateValue',
                'dueDateUnit',
                'invoiceDate',
                ...(isInvoiceSystemEnabled && hasProducts ? ['entries'] : []),
            ]);

            if (!isEditMode) {
                if (hasEmptyFields) {
                    Snackbar.showToastNotification({
                        message: t('Invoice:error:required_fields_missing'),
                        options: {
                            variant: 'error',
                        },
                    });
                    return;
                }

                for (const entry of entries) {
                    if (!entry.horseId) {
                        hasHorseAssigned = false;
                        break;
                    }

                    const procedures = await entry.entryProcedures.fetchCount();
                    if (!procedures) {
                        hasProceduresAssigned = false;
                        break;
                    }
                }
            }

            if (!hasHorseAssigned) {
                Snackbar.showToastNotification({
                    message: t('Invoice:alert:missing_horse_message'),
                    options: {
                        variant: 'info',
                    },
                });
                return;
            }

            if (!hasProceduresAssigned && !hasProducts) {
                Snackbar.showToastNotification({
                    message: isInvoiceSystemEnabled
                        ? t('Invoice:alert:no_entries_or_products_message')
                        : t('Invoice:alert:no_procedures_message'),
                    options: {
                        variant: 'info',
                    },
                });
                return;
            }
            addUpdateInvoice(values);
        },
        [addUpdateInvoice, isEditMode, isInvoiceSystemEnabled, t],
    );

    const form = useFormik<AddEditInvoiceFormValues>({
        enableReinitialize: true,
        initialValues,
        onSubmit: handleSubmit,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: false,
        validationSchema: null,
    });

    const getEntriesToSelectQuery = useCallback(async () => {
        if (form.values.contact) {
            const horses = await form.values.contact.horses.fetch();
            setEntriesToSelectQuery(
                entriesService.getEntriesForHorsesQuery(horses),
            );
        }
    }, [form.values.contact, entriesService]);

    useEffect(() => {
        getEntriesToSelectQuery();
    }, [getEntriesToSelectQuery]);

    const handleRemoveInvoice = useCallback(async () => {
        try {
            if (!invoice) {
                throw new Error();
            }

            const invoiceId = invoice.id;

            await invoiceService.deleteByID(invoiceId);

            InvoicesAnalytics.logUserDeletedInvoice(FirebaseAnalytics.logEvent);

            Snackbar.showToastNotification({
                message: t('App:Messages:has_been_removed_successfully', {
                    entity: t('Entities:invoice'),
                }),
            });

            close();

            if (onRemove) {
                onRemove();
            }
        } catch {
            Snackbar.showToastNotification({
                message: t('App:Messages:something_went_wrong'),
                options: {
                    variant: 'error',
                },
            });
        }
    }, [close, invoice, invoiceService, onRemove, t]);

    const onConfirmRemoveContact = useCallback(() => {
        form.setFieldValue('entries', []);
        form.setFieldValue('contact', null);
    }, [form]);

    const onRemoveContact = useCallback(() => {
        if (!form.values.entries.length) {
            form.setFieldValue('contact', null);
        } else {
            openRemoveContactAlert();
        }
    }, [form, openRemoveContactAlert]);

    const onSelectContact = useCallback(
        async (contact: ContactModel) => {
            if (form.values.contact?.id !== contact.id) {
                const entriesToKeep: EntryModel[] = [];

                for (const entry of form.values.entries) {
                    const entryHorse = await entry.horse.fetch();

                    if (!entryHorse) {
                        entriesToKeep.push(entry);
                    }
                }

                form.setFieldValue('entries', entriesToKeep);
            }

            form.setFieldValue('contact', contact);
        },
        [form],
    );

    const onDateChanged = useCallback(
        (value) => {
            hasUserSetDueDateRef.current = true;
            form.setFieldValue('dueDate', value);
        },
        [form],
    );

    const handleApplyTaxFieldChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const { value } = event.target;

            form.setFieldValue('applyTax', value);

            storeItemInLocalStorage(
                WEB_STORAGE_KEYS.apply_tax_to_invoice,
                value === 'included',
            );
        },
        [form],
    );

    const onInvoiceDateChanged = useCallback(
        (value) => {
            form.setFieldValue('invoiceDate', value);
            if (value && form.values.dueDateValue) {
                const newDueDate = value
                    .clone()
                    .add(form.values.dueDateValue, form.values.dueDateUnit);
                form.setFieldValue('dueDate', newDueDate);
            }
        },
        [form],
    );

    const onDueDateValueChanged = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const raw = event.target.value;
            const value = Math.max(
                1,
                Math.min(
                    Number(raw),
                    form.values.dueDateUnit === 'weeks'
                        ? MAX_DUE_WEEKS
                        : MAX_DUE_DAYS,
                ),
            );
            form.setFieldValue('dueDateValue', value);
            const newDueDate = form.values.invoiceDate
                .clone()
                .add(value, form.values.dueDateUnit);
            form.setFieldValue('dueDate', newDueDate);
        },
        [form],
    );

    const onDueDateUnitChanged = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const unit = event.target.value as DueDateUnit;
            const maxVal = unit === 'weeks' ? MAX_DUE_WEEKS : MAX_DUE_DAYS;
            const clampedValue = Math.min(form.values.dueDateValue, maxVal);
            form.setFieldValue('dueDateUnit', unit);
            form.setFieldValue('dueDateValue', clampedValue);
            const newDueDate = form.values.invoiceDate
                .clone()
                .add(clampedValue, unit);
            form.setFieldValue('dueDate', newDueDate);
        },
        [form],
    );

    const onFooterToggleChanged = useCallback(() => {
        const newValue = !form.values.useCustomFooter;
        form.setFieldValue('useCustomFooter', newValue);
        if (!newValue) {
            form.setFieldValue(
                'footerText',
                organisationData?.footerMessage || '',
            );
        }
    }, [form, organisationData?.footerMessage]);

    const onFooterTextChanged = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            form.setFieldValue('footerText', event.target.value);
        },
        [form],
    );

    const onSelectProducts = useCallback(
        (products: ParsedInvoiceProduct[]) => {
            form.setFieldValue('selectedProducts', products);
        },
        [form],
    );

    const onRemoveProduct = useCallback(
        (productIndex: number) => {
            const updated = [...(form.values.selectedProducts || [])];
            updated.splice(productIndex, 1);
            form.setFieldValue('selectedProducts', updated);
        },
        [form],
    );

    const onProductQuantityChanged = useCallback(
        (productIndex: number, quantity: number) => {
            const updated = [...(form.values.selectedProducts || [])];
            if (updated[productIndex]) {
                updated[productIndex] = {
                    ...updated[productIndex],
                    quantity: Math.max(1, quantity),
                };
                form.setFieldValue('selectedProducts', updated);
            }
        },
        [form],
    );

    const [invoiceFinancials, setInvoiceFinancials] =
        useState<InvoiceFinancialDetails>({
            subtotalSum: 0,
            totalSum: 0,
            proceduresSum: 0,
            proceduresTax: 0,
            productsSum: 0,
            productsTax: 0,
        });

    const calculateFinancials = useCallback(async () => {
        const procedures: { price: string; quantity: number }[] = [];

        for (const entry of form.values.entries) {
            const entryProcedures = await entry.entryProcedures.fetch();
            for (const proc of entryProcedures) {
                procedures.push({
                    price: proc.price || '0',
                    quantity: proc.quantity || 1,
                });
            }
        }

        const details = calculateInvoiceDetailsFromItems({
            taxRate:
                form.values.applyTax === 'included'
                    ? Number(form.values.taxRate || 0)
                    : 0,
            productsTaxRate:
                form.values.applyTax === 'included'
                    ? Number(form.values.productsTaxRate || 0)
                    : 0,
            procedures,
            products: form.values.selectedProducts,
        });

        setInvoiceFinancials(details);
    }, [
        form.values.entries,
        form.values.selectedProducts,
        form.values.applyTax,
        form.values.taxRate,
        form.values.productsTaxRate,
    ]);

    useEffect(() => {
        if (isInvoiceSystemEnabled) {
            calculateFinancials();
        }
    }, [calculateFinancials, isInvoiceSystemEnabled]);

    const currencySymbol = userRegion?.currencySymbol || '$';

    const renderDateTimePickerInput = useCallback(
        (params) => (
            <TextField
                {...params}
                fullWidth
                error={!!form.errors['dueDate'] && !!form.touched['dueDate']}
                helperText={
                    form.touched['dueDate'] ? form.errors['dueDate'] : ''
                }
                onBlur={form.handleBlur}
                name={'dueDate'}
                id={'dueDate'}
                data-test-id={'AddEditInvoiceForm-DueDate'}
                aria-readonly={false}
            />
        ),
        [form.errors, form.handleBlur, form.touched],
    );

    const entryValidation = useCallback(
        (entry: EntryModel) => !entry.invoiceId,
        [],
    );

    const onRemoveEntry = useCallback(
        (entry: EntryModel) => {
            const previousEntries = form.values.entries;
            const newEntries = previousEntries.filter((e) => e.id !== entry.id);
            form.setFieldValue('entries', newEntries);
        },
        [form],
    );

    const onSelectEntries = useCallback(
        (entries: EntryModel[]) => {
            form.setFieldValue('entries', entries);
        },
        [form],
    );

    const handleSelectedEntryContact = useCallback(
        (contact: ContactModel) => setPreSelectedContact(contact),
        [],
    );

    const cancelEntryContactSelection = useCallback(() => {
        closeSelectEntryContactModal();
        close();
    }, [close, closeSelectEntryContactModal]);

    const { handleFormCancelButtonClick, renderCancelAlert, showCancelAlert } =
        useCancelEntityFormAlert({
            closeEntityForm: close,
            shouldShowAlertOnCancel: form.dirty,
        });

    useEscapeButton(handleFormCancelButtonClick);

    const isUSRegion = regionStore.getRegion() === CountryCode.unitedStates;

    const dateInputFormat = isUSRegion
        ? 'dddd MMMM Do YYYY'
        : `dddd, MMMM DD [${t(
              'AddEntryForm:labels:date_input_placeholder',
          )}] YYYY`;

    return (
        <Modal
            isOpen
            center
            disableRestoreFocus
            testID="AddEditInvoiceModal"
            styles="relative py-0 w-[768px] max-h-[90vh] flex flex-col"
        >
            <BackdropLoader
                isLoading={isSyncInProgress && isCreating}
                sx={{
                    position: 'absolute',
                    borderRadius: '1rem',
                    backgroundColor: COLOR.white60,
                    color: COLOR.ebonyClay,
                }}
                loadingText={t(
                    isEditMode
                        ? 'Invoice:edit_loading_text'
                        : 'Invoice:add_loading_text',
                )}
            />
            <AddEditModalHeader
                backgroundColor={ENTITY_COLOR.invoices}
                cancel={handleFormCancelButtonClick}
                save={form.handleSubmit}
                testIdPrefix="AddEditInvoiceForm"
                title={t(
                    isEditMode ? 'Invoice:edit:view_title' : 'Invoice:new',
                )}
                teamName={
                    userOrganisations && userOrganisations?.length > 1
                        ? userOrganisation?.name || ''
                        : undefined
                }
                avatarUrl={
                    userOrganisations && userOrganisations?.length > 1
                        ? getImageDisplayUrl(teamAvatar) ?? ''
                        : undefined
                }
                icon={<InvoiceIcon color={COLOR.white} />}
            />
            <Box className="px-12 py-6 overflow-y-auto">
                <Box className="flex flex-col gap-5">
                    <SelectContactsSection
                        disabled={isEditMode}
                        onRemoveContact={onRemoveContact}
                        onSelectContacts={onSelectContact}
                        selectedContacts={
                            form.values.contact ? [form.values.contact] : []
                        }
                        CreateContactModal={AddEditContactModal}
                        testIdPrefix="AddEditInvoiceForm"
                        multiselect={false}
                        allowHorsesSelection
                        CreateHorseModal={AddEditHorseModal}
                        buttonsPosition="top"
                    />
                    {isInvoiceSystemEnabled ? (
                        <>
                            <Divider />
                            <Box className="flex flex-col gap-3">
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                >
                                    {t('Invoice:labels:applyTax')}
                                </Typography>
                                <TextField
                                    select
                                    id="applyTax"
                                    label={t('Invoice:labels:applyTax')}
                                    error={
                                        !!form.errors.applyTax &&
                                        !!form.touched.applyTax
                                    }
                                    helperText={
                                        form.touched.applyTax
                                            ? form.errors.applyTax
                                            : ''
                                    }
                                    onBlur={form.handleBlur}
                                    onChange={handleApplyTaxFieldChange}
                                    name="applyTax"
                                    value={form.values.applyTax}
                                    data-test-id="AddEditInvoiceForm-ApplyTax"
                                    fullWidth
                                    disabled={isEditMode}
                                >
                                    {invoiceTaxesList.map((invoiceTax) => (
                                        <MenuItem
                                            key={invoiceTax.value}
                                            value={invoiceTax.value}
                                            data-test-id={`AddEditInvoiceForm-Tax${invoiceTax.name}`}
                                        >
                                            {invoiceTax.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                            <Divider />
                            <Box className="flex flex-col gap-3">
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                >
                                    {t('Invoice:labels:date')}
                                </Typography>
                                <DesktopDatePicker
                                    label={t('Invoice:labels:date')}
                                    value={form.values.invoiceDate}
                                    onChange={onInvoiceDateChanged}
                                    inputFormat={dateInputFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            data-test-id="AddEditInvoiceForm-InvoiceDate"
                                        />
                                    )}
                                    disableMaskedInput
                                />
                            </Box>
                            <Divider />
                            <Box className="flex flex-col gap-3">
                                <Typography
                                    variant="subtitle1"
                                    fontWeight="bold"
                                >
                                    {t('Invoice:labels:due')}
                                </Typography>
                                <Box className="flex gap-3">
                                    <TextField
                                        id="dueDateValue"
                                        label={t('Invoice:labels:amount')}
                                        type="number"
                                        value={form.values.dueDateValue}
                                        onChange={onDueDateValueChanged}
                                        inputProps={{
                                            min: 1,
                                            max:
                                                form.values.dueDateUnit ===
                                                'weeks'
                                                    ? MAX_DUE_WEEKS
                                                    : MAX_DUE_DAYS,
                                        }}
                                        sx={{ flex: 1 }}
                                        data-test-id="AddEditInvoiceForm-DueDateValue"
                                    />
                                    <TextField
                                        select
                                        id="dueDateUnit"
                                        label={t('Invoice:labels:duration')}
                                        value={form.values.dueDateUnit}
                                        onChange={onDueDateUnitChanged}
                                        sx={{ flex: 1 }}
                                        data-test-id="AddEditInvoiceForm-DueDateUnit"
                                    >
                                        <MenuItem value="days">
                                            {t('Invoice:time_options:days')}
                                        </MenuItem>
                                        <MenuItem value="weeks">
                                            {t('Invoice:time_options:weeks')}
                                        </MenuItem>
                                    </TextField>
                                </Box>
                                <DesktopDatePicker
                                    label={t('Invoice:labels:date')}
                                    value={form.values.dueDate}
                                    onChange={onDateChanged}
                                    inputFormat={dateInputFormat}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            data-test-id="AddEditInvoiceForm-DueDateCalculated"
                                        />
                                    )}
                                    readOnly
                                    disableMaskedInput
                                />
                            </Box>
                        </>
                    ) : (
                        <Box className="flex gap-3">
                            <TextField
                                select
                                id="applyTax"
                                label={t('Invoice:labels:applyTax')}
                                error={
                                    !!form.errors.applyTax &&
                                    !!form.touched.applyTax
                                }
                                helperText={
                                    form.touched.applyTax
                                        ? form.errors.applyTax
                                        : ''
                                }
                                onBlur={form.handleBlur}
                                onChange={handleApplyTaxFieldChange}
                                name="applyTax"
                                value={form.values.applyTax}
                                data-test-id="AddEditInvoiceForm-ApplyTax"
                                sx={{ flex: 1 }}
                                disabled={isEditMode}
                            >
                                {invoiceTaxesList.map((invoiceTax) => (
                                    <MenuItem
                                        key={invoiceTax.value}
                                        value={invoiceTax.value}
                                        data-test-id={`AddEditInvoiceForm-Tax${invoiceTax.name}`}
                                    >
                                        {invoiceTax.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Box sx={{ flex: 1 }}>
                                <DesktopDatePicker
                                    label={t('Invoice:labels:dueDate')}
                                    value={form.values.dueDate}
                                    onChange={onDateChanged}
                                    inputFormat={dateInputFormat}
                                    renderInput={renderDateTimePickerInput}
                                    onClose={() =>
                                        form.validateField('dueDate')
                                    }
                                    disableMaskedInput
                                />
                            </Box>
                        </Box>
                    )}
                    {isInvoiceSystemEnabled && <Divider />}
                    <FormControlLabel
                        checked={form.values.attachReport}
                        className="min-w-0 mb-5"
                        control={
                            <Switch
                                data-test-id="AddEditInvoiceForm-AttachReportInput"
                                id="attachReport"
                                name="attachReport"
                                value={form.values.attachReport}
                                onChange={form.handleChange}
                                onBlur={form.handleBlur}
                            />
                        }
                        label={t('Invoice:labels:attachReport')}
                    />
                    {isInvoiceSystemEnabled && <Divider />}
                    <SelectEntriesSection
                        onRemoveEntry={onRemoveEntry}
                        onSelectEntries={onSelectEntries}
                        disabled={
                            form.values.contact === null ||
                            (!!isEditMode &&
                                !canEditInvoiceEntries(invoice?.status))
                        }
                        testIdPrefix="AddEditInvoiceForm"
                        CreateEntryModal={AddEditEntryModal}
                        selectedEntries={form.values.entries}
                        selectCondition={entryValidation}
                        conditionWarning={t('Entry:invoiced_entry_warning')}
                        entriesToSelect={entriesToSelectQuery || undefined}
                        entriesAllowedHorses={form.values.contact?.horses}
                        buttonsPosition={
                            isInvoiceSystemEnabled ? 'top' : 'bottom'
                        }
                        sectionLabel={
                            isInvoiceSystemEnabled
                                ? t('Invoice:section:entries_procedures')
                                : undefined
                        }
                        financialDetails={
                            isInvoiceSystemEnabled
                                ? {
                                      currency: currencySymbol,
                                      subtotal:
                                          invoiceFinancials.proceduresSum.toFixed(
                                              2,
                                          ),
                                      subtotalText: t(
                                          'InvoiceSystem:invoice_summary:subtotal_procedures',
                                      ),
                                      tax: '0',
                                      taxRate: '0',
                                  }
                                : undefined
                        }
                        showTax={
                            form.values.applyTax === 'included' &&
                            !accountingProvider
                        }
                        taxRate={form.values.taxRate}
                        defaultTaxRate={organisationData?.taxRate}
                        onTaxRateChange={(value) =>
                            form.setFieldValue('taxRate', value)
                        }
                        calculatedTax={invoiceFinancials.proceduresTax}
                        currencySymbol={currencySymbol}
                        onEntryEdited={calculateFinancials}
                    />
                    {isInvoiceSystemEnabled && (
                        <>
                            <Divider />
                            <SelectInvoicingProductsSection
                                onRemoveProduct={onRemoveProduct}
                                onSelectProducts={onSelectProducts}
                                onProductQuantityChanged={
                                    onProductQuantityChanged
                                }
                                selectedProducts={
                                    form.values.selectedProducts || []
                                }
                                testIdPrefix="AddEditInvoiceForm"
                                disabled={
                                    form.values.contact === null || !!isEditMode
                                }
                                currencySymbol={currencySymbol}
                                buttonsPosition="top"
                                sectionLabel={t('Invoice:section:products')}
                                allowCreate
                                userId={userProfileData?.id || ''}
                                organisationId={userOrganisation?.id || ''}
                                subtotal={invoiceFinancials.productsSum.toFixed(
                                    2,
                                )}
                                showTax={
                                    form.values.applyTax === 'included' &&
                                    !accountingProvider
                                }
                                productsTaxRate={form.values.productsTaxRate}
                                defaultProductsTaxRate={
                                    organisationData?.productsTaxRate
                                }
                                onProductsTaxRateChange={(value) =>
                                    form.setFieldValue('productsTaxRate', value)
                                }
                                calculatedProductsTax={
                                    invoiceFinancials.productsTax
                                }
                            />
                            <Divider />
                            <Box className="flex flex-col gap-2">
                                <Box className="flex items-center justify-between">
                                    <Typography
                                        variant="subtitle2"
                                        fontWeight="bold"
                                    >
                                        {t('Invoice:labels:customise_footer')}
                                    </Typography>
                                    <Switch
                                        checked={form.values.useCustomFooter}
                                        onChange={onFooterToggleChanged}
                                        data-test-id="AddEditInvoiceForm-FooterToggle"
                                    />
                                </Box>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                >
                                    {t('Invoice:labels:footer_description')}
                                </Typography>
                                {form.values.useCustomFooter && (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={form.values.footerText}
                                        onChange={onFooterTextChanged}
                                        data-test-id="AddEditInvoiceForm-FooterText"
                                    />
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
            {isInvoiceSystemEnabled && (
                <Box className="sticky bottom-0">
                    <Box
                        sx={{
                            height: 5,
                            background: `linear-gradient(90deg, ${addAlphaToColor(
                                ENTITY_COLOR.invoices,
                                0.2,
                            )} 0%, ${addAlphaToColor(
                                ENTITY_COLOR.invoices,
                                0.8,
                            )} 50%, ${addAlphaToColor(
                                ENTITY_COLOR.invoices,
                                0.2,
                            )} 100%)`,
                        }}
                    />
                    <Box className="flex items-center justify-between px-12 py-4">
                        <Box sx={{ opacity: 0.3 }}>
                            <InvoiceIcon color={ENTITY_COLOR.invoices} />
                        </Box>
                        <Box className="flex items-center gap-2">
                            <Typography
                                variant="body2"
                                color={
                                    invoiceFinancials.totalSum > 0
                                        ? 'textPrimary'
                                        : 'textSecondary'
                                }
                            >
                                {t('Invoice:labels:invoice_total')}
                            </Typography>
                            <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={
                                    invoiceFinancials.totalSum > 0
                                        ? 'textPrimary'
                                        : 'textSecondary'
                                }
                            >
                                {currencySymbol}
                                {invoiceFinancials.totalSum.toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}
            <RemoveContactAlert
                isOpen={isRemoveContactAlertOpen}
                onClose={closeRemoveContactAlert}
                onConfirm={onConfirmRemoveContact}
            />
            {isEditMode && (
                <>
                    <TooltipSyncButton
                        tooltip={t('AddEditModalHeader:button_tooltip', {
                            defaultValue:
                                'Please wait for the synchronization to complete',
                        })}
                        showTooltip={isSyncInProgress}
                        onClick={openRemoveInvoiceAlert}
                        disabled={isSyncInProgress}
                        testID="AddInvoiceForm-RemoveButton"
                        sx={{ mt: 3, alignSelf: 'center' }}
                        stylesAt="container"
                        color="error"
                        startIcon={<DeleteOutlineRoundedIcon />}
                    >
                        {t('Invoice:button:remove')}
                    </TooltipSyncButton>
                    <RemoveEntityAlert
                        dialogText={t('Invoice:delete_invoice_confirmation')}
                        isOpen={isRemoveInvoiceAlertOpen}
                        onClose={closeRemoveInvoiceAlert}
                        onRemove={handleRemoveInvoice}
                        testID="RemoveInvoiceAlert"
                    />
                </>
            )}
            {isSelectEntryContactModalOpen ? (
                <SelectContactsModal
                    close={closeSelectEntryContactModal}
                    cancel={cancelEntryContactSelection}
                    isOpen={isSelectEntryContactModalOpen}
                    onSelectContacts={handleSelectedEntryContact}
                    selectedContacts={[]}
                    multiSelect={false}
                    contactsToSelect={preSelectedEntryHorse?.contacts}
                />
            ) : null}
            {showCancelAlert ? renderCancelAlert() : null}
        </Modal>
    );
}

export default AddEditInvoiceModal;
